/**
 * ChatbotAvatar Component
 * Clean SVG-based robot icon with consistent proportions
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import './ChatbotAvatar.css';

interface ChatbotAvatarProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
  showChatBubble?: boolean;
}

const ChatbotAvatar: React.FC<ChatbotAvatarProps> = ({ 
  size = 'medium',
  className = '',
  showChatBubble = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [rotation, setRotation] = useState(0);
  const animationFrameRef = useRef<number | null>(null);

  // Size mapping
  const sizeMap = {
    small: 60,
    medium: 70,
    large: 80
  };
  
  const avatarSize = sizeMap[size];

  // Mouse tracking for head rotation
  const calculateRotation = useCallback((mouseX: number, mouseY: number) => {
    if (!containerRef.current) return 0;

    const rect = containerRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const angle = Math.atan2(mouseY - centerY, mouseX - centerX);
    let degrees = angle * (180 / Math.PI);
    
    const maxRotation = 15;
    degrees = Math.max(-maxRotation, Math.min(maxRotation, degrees));
    
    return degrees;
  }, []);

  const handleMouseMove = useCallback((event: MouseEvent) => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      const newRotation = calculateRotation(event.clientX, event.clientY);
      setRotation(newRotation);
    });
  }, [calculateRotation]);

  const handleMouseLeave = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    setRotation(0);
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleMouseMove, handleMouseLeave]);

  return (
    <div 
      ref={containerRef}
      className={`chatbot-avatar chatbot-avatar--${size} ${className}`}
      style={{ width: avatarSize, height: avatarSize }}
    >
      {showChatBubble && (
        <div className="chatbot-avatar__chat-bubble">
          <div className="chatbot-avatar__bubble-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
      
      <svg 
        viewBox="0 0 100 100" 
        width="100%" 
        height="100%"
        className="chatbot-avatar__svg"
      >
        {/* Outer glow */}
        <defs>
          <radialGradient id="glow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#a78bfa" stopOpacity="0" />
          </radialGradient>
          <filter id="shadow">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3"/>
          </filter>
        </defs>

        {/* Glow effect */}
        <circle cx="50" cy="50" r="45" fill="url(#glow)" />

        {/* Rotating head group */}
        <g transform={`rotate(${rotation} 50 50)`}>
          {/* Ears */}
          <polygon 
            points="30,35 25,25 35,25" 
            fill="#a78bfa" 
            filter="url(#shadow)"
          />
          <polygon 
            points="70,35 65,25 75,25" 
            fill="#a78bfa" 
            filter="url(#shadow)"
          />

          {/* Head */}
          <circle 
            cx="50" 
            cy="45" 
            r="22" 
            fill="#7c3aed" 
            filter="url(#shadow)"
          />
          
          {/* Face screen */}
          <circle cx="50" cy="45" r="18" fill="#1e1b4b" />

          {/* Eyes */}
          <rect 
            x="38" 
            y="40" 
            width="6" 
            height="8" 
            rx="1"
            fill="#10b981" 
          />
          <rect 
            x="56" 
            y="40" 
            width="6" 
            height="8" 
            rx="1"
            fill="#10b981" 
          />

          {/* Eye pupils */}
          <circle cx="41" cy="45" r="2" fill="#064e3b" />
          <circle cx="59" cy="45" r="2" fill="#064e3b" />

          {/* Smile */}
          <path 
            d="M 42 52 Q 50 55 58 52" 
            stroke="white" 
            strokeWidth="2" 
            fill="none" 
            strokeLinecap="round"
          />
        </g>

        {/* Body (doesn't rotate) */}
        <ellipse 
          cx="50" 
          cy="72" 
          rx="16" 
          ry="12" 
          fill="#6366f1" 
          filter="url(#shadow)"
        />

        {/* Arms */}
        <circle cx="32" cy="72" r="6" fill="#bfdbfe" />
        <circle cx="68" cy="72" r="6" fill="#bfdbfe" />

        {/* Badge */}
        <circle cx="50" cy="72" r="5" fill="#f472b6" />
        <circle cx="50" cy="72" r="2" fill="white" />

        {/* Booster */}
        <ellipse cx="50" cy="85" rx="10" ry="5" fill="#fb923c" />
        
        {/* Flame */}
        <ellipse 
          cx="50" 
          cy="88" 
          rx="4" 
          ry="3" 
          fill="#fbbf24" 
          opacity="0.8"
        />
      </svg>
    </div>
  );
};

export default ChatbotAvatar;

